import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Navigate } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { NBA_MARKET_SECONDARY } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { createCardSorts } from '@sorare/core/src/lib/algolia';
import { metadatas } from '@sorare/core/src/lib/seo/nba';

import { MarketStackTitle } from '@sorare/marketplace/src/components/market/MarketStackTitle';
import { MarketRoot } from '@sorare/marketplace/src/components/market/ui';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { secondaryMarketStackedFilters } from '@sorare/marketplace/src/searchCards/nba/cardFilters';

import { NBACardSearch } from 'components/searchCards/NBACardSearch';

import type {
  NBASecondaryMarketStackPlayerQuery,
  NBASecondaryMarketStackPlayerQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const NBA_SECONDARY_MARKET_STACK_PLAYER_QUERY = gql`
  query NBASecondaryMarketStackPlayerQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
    }
  }
` as TypedDocumentNode<
  NBASecondaryMarketStackPlayerQuery,
  NBASecondaryMarketStackPlayerQueryVariables
>;

const SecondaryMarketStack = () => {
  const { playerSlug, rarity } = useTypedParams<PageParams>();
  const { data, loading } = useQuery(NBA_SECONDARY_MARKET_STACK_PLAYER_QUERY, {
    variables: {
      slug: playerSlug,
    },
  });

  useTitleAndDescription(
    metadatas.transfers.title,
    metadatas.transfers.description
  );

  const filters = useDefaultFilters({
    secondary: true,
    playerSlug,
    rarity,
    onlySettlableCards: true,
  });

  const cardSorts = createCardSorts({
    indexPrefix: 'Cards On Sale',
    withHighestAverageScore: true,
    withHighestPrice: false,
    withEndingSoon: false,
  });

  const defaultSort = 'Cards On Sale Lowest Price';

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!data?.anyPlayer || !Object.keys(Rarity).includes(rarity)) {
    return <Navigate to={NBA_MARKET_SECONDARY} />;
  }

  return (
    <MarketRoot>
      <Portal id="page-header-title-breadcrumb">
        <MarketStackTitle
          player={data.anyPlayer}
          rarity={rarity as Rarity}
          sport={Sport.NBA}
        />
      </Portal>
      <NBACardSearch
        sorts={cardSorts}
        defaultSort={defaultSort}
        cardFilters={secondaryMarketStackedFilters}
        defaultFilters={filters}
        analyticsTags={['TransferMarketStack', 'NBA']}
        removeEndedSingleSaleOffers
        topic={{
          type: 'stack',
          label: data.anyPlayer.displayName,
        }}
      />
    </MarketRoot>
  );
};
export default SecondaryMarketStack;
